<script setup>
import { ref, computed } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useDevicesStore } from "@/stores/devices";
import { MODAL_ACTIONS } from "@/utils/constants/modalActions";

const emit = defineEmits(["callDeviceApps"]);
const uiStore = useUiStore();
const devicesStore = useDevicesStore();
const companyId = useCompanyStore().companyId;
const user = JSON.parse(localStorage.getItem("userDetails"));

const isSubmitted = ref(false);

const getModalInfo = computed(() => uiStore.modalData);
const getModalAction = computed(() => uiStore.modalAction);
const getDeviceDataObject = computed(() => devicesStore.deviceDataObject);

const ACTIONS = {
  [MODAL_ACTIONS.CLEAR_APP_CACHE]: {
    title: "Clear App Cache",
    verb: "Clear Cache",
    buttonText: "Clear",
    storeMethod: "clearAppCache",
    payloadKey: "packageName",
  },
  [MODAL_ACTIONS.CLEAR_APP_DATA]: {
    title: "Clear App Data",
    verb: "Clear Data",
    buttonText: "Clear",
    storeMethod: "clearAppData",
    payloadKey: "packageName",
  },
  [MODAL_ACTIONS.FORCE_STOP_APP]: {
    title: "Force Stop App",
    verb: "Force Stop",
    buttonText: "Stop",
    storeMethod: "forceStopApp",
    payloadKey: "packageName",
  },
  [MODAL_ACTIONS.LAUNCH_APP]: {
    title: "Launch App",
    verb: "Launch",
    buttonText: "Launch",
    storeMethod: "launchApp",
    payloadKey: "packageName",
  },
  [MODAL_ACTIONS.UNINSTALL_APP]: {
    title: "Uninstall App",
    verb: "Uninstall",
    buttonText: "Uninstall",
    storeMethod: "uninstallApp",
    payloadKey: "packages",
    payloadIsArray: true,
  },
};

const currentAction = computed(() => ACTIONS[getModalAction.value] ?? null);
const isOpen = computed(() => !!currentAction.value);
const appName = computed(() => getModalInfo.value?.name ?? "");

const resetStoreData = () => {
  uiStore.resetModalStore();
  emit("callDeviceApps");
};

const onSubmit = () => {
  const conf = currentAction.value;
  if (!conf) return;
  isSubmitted.value = true;

  const packageId = getModalInfo.value?.packageId;
  const data = {
    entityId: companyId,
    kioskName: getDeviceDataObject.value?.name,
    macAddress: getDeviceDataObject.value?.mac_address,
    userName: `${user.first_name} ${user.last_name}`,
    userId: user.user_id,
    connectionType: "daemon",
    [conf.payloadKey]: conf.payloadIsArray ? [packageId] : packageId,
  };

  devicesStore[conf.storeMethod](data)
    .then(() => {
      isSubmitted.value = false;
      resetStoreData();
    })
    .catch(() => {
      isSubmitted.value = false;
    });
};
</script>

<template>
  <DModal
    v-if="isOpen"
    :visible="uiStore.modalState"
    @update:visible="(val) => { if (!val) resetStoreData(); }"
    :main_classes="[]"
    :dialog_classes="[]"
  >
    <template #title>{{ currentAction.title }}</template>

    <template #body>
      <div class="delete-text">
        <span>
          Are you sure you want to <b>"{{ currentAction.verb }}"</b> the app
          "{{ appName }}" ?
        </span>
      </div>
    </template>

    <template #footer>
      <div class="w-full">
        <div class="seperator text-center"></div>
        <div class="flex mt-20 justify-center">
          <button
            :disabled="isSubmitted"
            @click="onSubmit"
            type="button"
            class="btn btn-primary add-button mr-30"
          >
            {{ currentAction.buttonText }}
            <span
              v-if="isSubmitted"
              class="spinner-sm"
              role="status"
              aria-hidden="true"
            ></span>
          </button>
          <button
            @click="resetStoreData"
            type="button"
            class="btn btn-secondary cancel-button"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </DModal>
</template>
